import { useUser } from "@clerk/expo";
import { Trash2 } from "lucide-react-native";
import { FC, useCallback, useMemo, useState } from "react";

import { AdvancedSettings } from "@/components/questions";
import {
  Box,
  Button,
  ButtonText,
  DeleteDialog,
  HStack,
  Input,
  InputField,
  Pressable,
  Text,
  Textarea,
  TextareaInput,
  VStack,
} from "@/components/ui";
import { useLocalization } from "@/hooks";
import API from "@/hooks/api";
import {
  DraftCell,
  Question,
  SectionConfig,
  Section as SectionType,
  VariableDef,
} from "@/types/resources";
import { UseCase } from "@/types/utils";

import {
  CellsEditor,
  RotationControls,
  RotationField,
  VariablesEditor,
} from "./cells";
import {
  UseCaseConfigField,
  getUseCaseRequirements,
  useUseCaseLabels,
} from "./useCaseRequirements";

/** Binomial coefficient C(n, k) — number of distinct k-subsets of an
    n-set. Used to cap `cells_per_respondent` for MaxDiff so authors
    can't request more rotation cells than the rotator can produce
    (e.g. 4 items with 4 items per task → only 1 distinct cell). */
const combinations = (n: number, k: number): number => {
  if (k < 0 || k > n) return 0;
  if (k === 0 || k === n) return 1;
  const kk = Math.min(k, n - k);
  let result = 1;
  for (let i = 0; i < kk; i++) {
    result = (result * (n - i)) / (i + 1);
  }
  return Math.floor(result);
};

/** Rotation knobs shown for free sections. Only `cells_per_respondent` is
    exposed — items-per-cell (multiple items per screen) stays reserved for
    use-case templates that opt into it via `requirements.config_fields`. */
const FREE_ROTATION_FIELDS: RotationField[] = ["cells_per_respondent"];

const emptyCell = (variables: VariableDef[]): DraftCell =>
  variables.reduce<DraftCell>((cell, variable) => {
    cell[variable.name] = null;
    return cell;
  }, {});

/** A variable value counts as "filled" when it's a picked asset or a
    non-blank string (matches the backend's required-field emptiness check). */
const hasValue = (value: DraftCell[string]): boolean =>
  value !== null &&
  value !== undefined &&
  (typeof value !== "string" || value.trim() !== "");

interface SectionDraft {
  cells: DraftCell[];
  config: SectionConfig | null;
  description: string;
  title: string;
  variables: VariableDef[];
}

interface SectionEditorProps {
  disabled?: boolean;
  /** Called when the user cancels — for create mode this just unmounts the
      form, no API call. For edit mode the parent typically toggles
      `editing` back off. */
  onCancel: () => void;
  /** Edit-mode only: fired after a successful DELETE. Unused in create
      mode (delete button is hidden) but required so callers don't have
      to special-case the prop. */
  onDeleted: () => void;
  /** Called with the saved section and its current questions. Most callers
      only consume the section, but the questions array is included so
      sites that mirror server state (e.g. the study editor) can reconcile
      without a second fetch. */
  onSaved: (result: { questions: Question[]; section: SectionType }) => void;
  /** Pass a section to edit it; omit for create mode, where Save fires a
      POST instead of a PUT and the delete affordance is hidden. */
  section?: SectionType;
  studyId: string;
}

/**
 * Form body for a `Section` card — covers both creation (no `section` prop)
 * and edit. `Section` owns the card chrome and the collapsed display; this
 * component owns the whole form.
 *
 * The form always renders title + description. Below that it shows the
 * section's **variables** and **cells**:
 *  - **use-case** sections take their variable schema from the materialized
 *    template (`section.config.variables`) and render it read-only, then let
 *    the author fill the cells and (for templates that allow it) tune the
 *    rotation knobs.
 *  - **free** (custom) sections expose the whole thing — an editable variable
 *    schema, the cells, and the rotation knobs — under an "Advanced settings"
 *    accordion so the primary flow stays title + description.
 *
 * Save persists title, description, the cells (freshly-picked images uploaded
 * inline) and the config (field schema + rotation). It's disabled until every
 * required variable is filled in every non-empty cell — the frontend mirror of
 * the backend's `validate_cells`.
 */
const SectionEditor: FC<SectionEditorProps> = ({
  disabled = false,
  onCancel,
  onDeleted,
  onSaved,
  section,
  studyId,
}) => {
  const { i18n } = useLocalization();
  const { user } = useUser();
  const isAdmin = user?.publicMetadata?.admin === true;

  const isCreating = section === undefined;
  const useCase = section?.use_case ?? null;
  const isFree = useCase === null;
  const requirements = useMemo(
    () => getUseCaseRequirements(useCase),
    [useCase]
  );
  const labels = useUseCaseLabels(useCase);
  /* Use-case sections only expose the variables/cells UI when their template
     declares cell requirements. The item-pool use cases (Preference, Awareness,
     MaxDiff, TURF) author their items on the questions instead, so they report
     no requirements and never show cells — even if a legacy section still
     carries persisted variables/cells. */
  const useCaseHasCells =
    requirements.min_cells > 0 || requirements.config_fields.length > 0;
  const canDelete = !isCreating;
  const mediaType = useCase === UseCase.AdCreative || isFree ? "all" : "images";

  const buildInitialDraft = useCallback((): SectionDraft => {
    const variables = section?.config?.variables ?? [];
    const config = section?.config ?? null;
    if (!section) {
      return { cells: [], config, description: "", title: "", variables };
    }
    /* Pad the cells up to the use-case minimum so the editor always exposes
       at least the required number of authoring rows. */
    const cells: DraftCell[] = [...section.cells];
    while (cells.length < requirements.min_cells)
      cells.push(emptyCell(variables));
    return {
      cells,
      config,
      description: section.description ?? "",
      title: section.title,
      variables,
    };
  }, [requirements.min_cells, section]);

  const [draft, setDraft] = useState<SectionDraft>(buildInitialDraft);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const {
    createSection,
    error: createError,
    loading: creating,
    uploadProgress: createUploadProgress,
  } = API.Studies.useCreateSection(studyId);
  const {
    error: updateError,
    loading: updating,
    updateSection,
    uploadProgress: updateUploadProgress,
  } = API.Studies.useUpdateSection(studyId);
  const {
    deleteSection,
    error: deleteError,
    loading: deleting,
  } = API.Studies.useDeleteSection(studyId);

  const saving = isCreating ? creating : updating;
  const saveError = isCreating ? createError : updateError;
  const uploadProgress = isCreating
    ? createUploadProgress
    : updateUploadProgress;

  const updateDraft = useCallback(
    (patch: Partial<SectionDraft>) =>
      setDraft((prev) => ({ ...prev, ...patch })),
    []
  );

  /* Rotation knobs available for this section: the full set for free sections,
     the template-allowed subset otherwise. */
  const rotationFields = isFree
    ? FREE_ROTATION_FIELDS
    : (requirements.config_fields as RotationField[]);
  const itemsPerCellEditable = rotationFields.includes("items_per_cell");
  const itemsPerCell = draft.config?.items_per_cell ?? 1;

  /* Only variables that have actually been named produce a persisted cell
     key; unnamed rows in the editor are ignored (mirrors CellsEditor). */
  const namedVariables = useMemo(
    () => draft.variables.filter((variable) => variable.name.trim()),
    [draft.variables]
  );

  /* A cell is "filled" once any of its declared variables carries a value —
     this drives the dynamic upper bound of the rotation knobs (a respondent
     can't be shown more cells than actually exist). */
  const filledCellCount = useMemo(
    () =>
      draft.cells.filter((cell) =>
        namedVariables.some((variable) => hasValue(cell[variable.name]))
      ).length,
    [draft.cells, namedVariables]
  );

  const getBounds = useCallback(
    (field: UseCaseConfigField): { max: number; min: number } => {
      const staticBounds = requirements.field_bounds[field] ?? {
        max: 99,
        min: 1,
      };
      const effectiveFilled = Math.max(
        filledCellCount,
        requirements.min_cells,
        1
      );
      if (field === "cells_per_respondent" && itemsPerCellEditable) {
        return {
          max: Math.min(
            staticBounds.max,
            Math.max(1, combinations(effectiveFilled, itemsPerCell))
          ),
          min: staticBounds.min,
        };
      }
      return {
        max: Math.min(staticBounds.max, Math.max(1, effectiveFilled)),
        min: staticBounds.min,
      };
    },
    [
      filledCellCount,
      itemsPerCell,
      itemsPerCellEditable,
      requirements.field_bounds,
      requirements.min_cells,
    ]
  );

  /* Cells worth persisting: drop the fully-empty padding rows the editor adds
     up to `min_cells` — the backend only validates the cells we actually
     send, so blank trailing rows shouldn't fail the required-field check. */
  const persistedCells = useMemo(
    () =>
      draft.cells.filter((cell) =>
        namedVariables.some((variable) => hasValue(cell[variable.name]))
      ),
    [draft.cells, namedVariables]
  );

  /* Config carries the field schema the backend validates cells against, so
     it's only sent once there are named variables (no variables → no cells).
     Unnamed variables are stripped, and the rotation knobs are persisted at
     their effective (displayed) value — the stepper only shows `bounds.min` by
     default without writing it, so we materialize it here so the saved config
     matches what the editor shows. */
  const configToSave = useMemo<SectionConfig | null>(() => {
    if (namedVariables.length === 0) return null;
    const config: SectionConfig = {
      ...draft.config,
      variables: namedVariables,
    };
    for (const field of rotationFields) {
      const bounds = getBounds(field);
      const current = draft.config?.[field] ?? bounds.min;
      config[field] = Math.max(bounds.min, Math.min(bounds.max, current));
    }
    return config;
  }, [draft.config, getBounds, namedVariables, rotationFields]);

  /* Freshly-picked images awaiting upload (asset objects rather than stored
     S3 key strings) — drives the "Uploading N files…" button label. */
  const newMediaCount = useMemo(
    () =>
      persistedCells.reduce(
        (count, cell) =>
          count +
          Object.values(cell).filter(
            (value) => value !== null && typeof value !== "string"
          ).length,
        0
      ),
    [persistedCells]
  );

  /* Frontend mirror of the backend save rules: at least `min_cells` filled
     cells (use-case minimum; 0 for free sections), and every required variable
     filled in each of those cells. Empty padding rows are dropped, so an
     unfilled required cell fails the count rather than sneaking through. */
  const cellsValid = useMemo(() => {
    /* Item-pool use cases (no declared cell requirements) author their items on
       the questions, so there's nothing to validate here. */
    if (!isFree && !useCaseHasCells) return true;
    /* Free sections with no declared variables likewise have no cells. */
    if (namedVariables.length === 0) return true;
    if (persistedCells.length < requirements.min_cells) return false;
    const required = namedVariables.filter((variable) => variable.is_required);
    /* Validate every cell shown in the editor (not just the non-empty ones):
       a visible cell with an empty required field is invalid — otherwise a
       blank required cell would silently pass by being dropped. */
    return draft.cells.every((cell) =>
      required.every((variable) => hasValue(cell[variable.name]))
    );
  }, [
    draft.cells,
    isFree,
    namedVariables,
    persistedCells.length,
    requirements.min_cells,
    useCaseHasCells,
  ]);

  /* Variable names key the cell values, so two variables can't share one —
     mirrors the backend `validate_unique_variable_names` check. */
  const hasDuplicateVariableNames = useMemo(() => {
    const names = namedVariables.map((variable) => variable.name.trim());
    return new Set(names).size !== names.length;
  }, [namedVariables]);

  /* Names are referenced inline as `{{name}}` and key the cell values, so we
     keep them to letters and digits — mirrors the backend charset check. */
  const hasInvalidVariableNames = useMemo(
    () =>
      namedVariables.some((variable) => !/^[A-Za-z0-9]+$/.test(variable.name)),
    [namedVariables]
  );

  /* ── Save / cancel / delete ── */
  const handleSave = useCallback(async () => {
    if (
      !draft.title.trim() ||
      !cellsValid ||
      hasDuplicateVariableNames ||
      hasInvalidVariableNames
    )
      return;
    if (!section) {
      const created = await createSection({
        cells: persistedCells,
        config: configToSave,
        description: draft.description.trim() || null,
        title: draft.title.trim(),
      });
      if (created) onSaved(created);
      return;
    }
    const updated = await updateSection(section.id, {
      cells: persistedCells,
      config: configToSave,
      description: draft.description.trim() || null,
      ...(draft.title !== section.title ? { title: draft.title.trim() } : {}),
    });
    if (updated) onSaved(updated);
  }, [
    cellsValid,
    configToSave,
    createSection,
    draft.description,
    draft.title,
    hasDuplicateVariableNames,
    hasInvalidVariableNames,
    onSaved,
    persistedCells,
    section,
    updateSection,
  ]);

  const handleDelete = useCallback(async () => {
    if (!section) return;
    const success = await deleteSection(section.id);
    if (success) {
      onDeleted();
      setShowDeleteDialog(false);
    }
  }, [deleteSection, onDeleted, section]);

  const isSaveDisabled =
    !draft.title.trim() ||
    !cellsValid ||
    hasDuplicateVariableNames ||
    hasInvalidVariableNames;

  /* ── Renders ────────────────────────────────────────────── */

  const renderHeader = () => (
    <VStack className="gap-2">
      <FieldLabel label={i18n.t("studies.sectionEditor.title")} required />
      <VStack className="pl-4">
        <Input className="border border-outline-200 rounded-xl bg-background-50">
          <InputField
            autoFocus
            onChangeText={(value) => updateDraft({ title: value })}
            placeholder={i18n.t("studies.sectionEditor.titlePlaceholder")}
            value={draft.title}
          />
        </Input>
      </VStack>
    </VStack>
  );

  const renderDescription = () => (
    <VStack className="gap-2">
      <FieldLabel label={i18n.t("studies.sectionEditor.description")} />
      <VStack className="pl-4">
        <Textarea className="border border-outline-200 rounded-xl bg-background-50">
          <TextareaInput
            multiline
            numberOfLines={3}
            onChangeText={(value) => updateDraft({ description: value })}
            placeholder={i18n.t("studies.sectionEditor.descriptionPlaceholder")}
            value={draft.description}
          />
        </Textarea>
      </VStack>
    </VStack>
  );

  const renderCells = () => (
    <CellsEditor
      addLabel={isFree ? undefined : labels.addStimulus}
      cells={draft.cells}
      disabled={disabled}
      maxCells={isAdmin ? undefined : requirements.max_cells || undefined}
      mediaType={mediaType}
      minCells={requirements.min_cells}
      onChange={(cells) => updateDraft({ cells })}
      title={isFree ? undefined : labels.stimuli}
      variables={draft.variables}
    />
  );

  const renderRotation = () =>
    rotationFields.length > 0 && draft.variables.length > 0 ? (
      <RotationControls
        config={draft.config}
        fields={rotationFields}
        getBounds={getBounds}
        labels={labels}
        onChange={(config) =>
          updateDraft({
            config: { ...draft.config, ...config, variables: draft.variables },
          })
        }
      />
    ) : null;

  const renderBody = () => {
    if (isFree) {
      return (
        <AdvancedSettings
          defaultExpanded={
            namedVariables.length > 0 || persistedCells.length > 0
          }
        >
          <VStack className="gap-5">
            <VariablesEditor
              onChange={(variables) => updateDraft({ variables })}
              variables={draft.variables}
            />
            {renderCells()}
            {renderRotation()}
          </VStack>
        </AdvancedSettings>
      );
    }
    /* Item-pool use cases carry their items on the questions (authored as
       options), so there's nothing to show here beyond title + description. */
    if (!useCaseHasCells) return null;
    return (
      <>
        <VariablesEditor readOnly variables={draft.variables} />
        {renderCells()}
        {renderRotation()}
      </>
    );
  };

  const renderFooter = () => (
    <HStack className="justify-between items-center pt-3 mt-3 border-t border-outline-100">
      {canDelete ? (
        <Pressable
          className="p-2 rounded-lg hover:bg-error-50"
          onPress={() => setShowDeleteDialog(true)}
        >
          <Trash2 color="rgb(var(--color-error-600))" size={20} />
        </Pressable>
      ) : (
        <Box />
      )}
      <HStack className="gap-2">
        <Button
          isDisabled={saving}
          onPress={onCancel}
          size="sm"
          variant="outline"
        >
          <ButtonText>{i18n.t("misc.cancel")}</ButtonText>
        </Button>
        <Button
          className="bg-primary-500"
          isDisabled={isSaveDisabled || saving}
          onPress={handleSave}
          size="sm"
        >
          <ButtonText className="text-white">
            {uploadProgress > 0
              ? i18n.t("misc.uploadingMedia", {
                  count: newMediaCount,
                  percent: i18n.numberToPercentage(uploadProgress * 100, {
                    precision: 0,
                  }),
                })
              : saving
                ? i18n.t("misc.saving")
                : i18n.t("misc.save")}
          </ButtonText>
        </Button>
      </HStack>
    </HStack>
  );

  return (
    <>
      {renderHeader()}

      <VStack className="gap-5 mt-4">
        {renderDescription()}
        {renderBody()}
      </VStack>

      {renderFooter()}

      {saveError && (
        <Text className="text-error-600 text-xs mt-2 text-right">
          {saveError}
        </Text>
      )}

      {canDelete && (
        <DeleteDialog
          body={i18n.t("studies.confirmDeleteSectionMessage")}
          error={deleteError}
          isOpen={showDeleteDialog}
          loading={deleting}
          onCancel={() => setShowDeleteDialog(false)}
          onDelete={handleDelete}
          title={i18n.t("studies.confirmDeleteSection")}
        />
      )}
    </>
  );
};

/* ── Tiny presentational helper for field-section labels ─────── */

const FieldLabel: FC<{ label: string; required?: boolean }> = ({
  label,
  required = false,
}) => (
  <HStack className="items-center gap-1">
    <Text className="text-sm font-semibold text-typography-800">{label}</Text>
    {required && (
      <Text className="text-sm font-semibold text-error-500">*</Text>
    )}
  </HStack>
);

export default SectionEditor;
